import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ref, push } from "firebase/database";
import { rtdb } from "@/lib/firebase";
import { useScrollLock } from "@/lib/scrollLock";
import { getTranslatedText } from "@/lib/translations";
import type { ServiceAction } from "@/hooks/useRestaurant";

interface Props {
  open: boolean;
  onClose: () => void;
  restoId: string;
  qrId: string;
  table: string | number | null | undefined;
  services: ServiceAction[];
}

export const ServiceModal = ({
  open,
  onClose,
  restoId,
  qrId,
  table,
  services,
}: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setConfirmation(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Auto-dismiss confirmation toast.
  useEffect(() => {
    if (!confirmation) return;
    const id = window.setTimeout(() => {
      setConfirmation(null);
      onClose();
    }, 1800);
    return () => clearTimeout(id);
  }, [confirmation, onClose]);

  const sendRequest = async (service: ServiceAction): Promise<void> => {
    const localId = service.id ?? service.name;
    setPending(localId);
    const display = getTranslatedText(service as any, "name", i18n.language) || service.name;
    try {
      await push(ref(rtdb, `serviceRequests/${restoId}`), {
        serviceId: service.id ?? "",
        serviceName: service.name,
        serviceNameDisplay: display,
        serviceIcon: service.icon ?? "bell",
        printerId: service.printerId ?? "",
        table: table ?? "",
        qrId: qrId ?? "",
        createdAt: Date.now(),
        status: "pending",
      });
      setConfirmation(display);
    } catch (err) {
      console.error("[ServiceModal] push failed", err);
      setConfirmation(null);
    } finally {
      setPending(null);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const enabledServices = services.filter((s) => s.enabled !== false);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t("service.title", "Appeler un service")}
    >
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />
      <div className="relative w-full sm:max-w-md bg-bg rounded-t-modal sm:rounded-modal max-h-[85vh] flex flex-col animate-slide-up shadow-warm-lg pb-safe">
        <header className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h2 className="display-font text-xl font-semibold text-ink">
            {t("service.title", "Appeler un service")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink"
            aria-label="Fermer"
          >
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {confirmation ? (
            <div className="py-10 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-success/15 text-success flex items-center justify-center text-3xl mb-4" aria-hidden="true">
                ✓
              </div>
              <p className="display-font text-lg font-semibold text-ink">{confirmation}</p>
              <p className="text-sm text-ink-soft mt-1">
                {t("service.requestSent", "Votre demande a été envoyée")}
              </p>
            </div>
          ) : enabledServices.length === 0 ? (
            <div className="py-10 text-center text-ink-soft text-sm">
              Aucun service disponible pour le moment.
            </div>
          ) : (
            <ul className="grid grid-cols-2 gap-3">
              {enabledServices.map((service, idx) => {
                const localId = service.id ?? service.name;
                const display =
                  getTranslatedText(service as any, "name", i18n.language) || service.name;
                const isPending = pending === localId;
                return (
                  <li key={localId || idx}>
                    <button
                      type="button"
                      disabled={!!pending}
                      onClick={() => void sendRequest(service)}
                      className="w-full aspect-square flex flex-col items-center justify-center gap-2 p-3 rounded-button border-2 border-border bg-surface hover:border-warm/60 transition-colors disabled:opacity-50"
                    >
                      <span className="text-3xl" aria-hidden="true">🛎️</span>
                      <span className="text-sm font-medium text-ink text-center leading-tight">
                        {display}
                      </span>
                      {isPending && (
                        <span className="text-[10px] text-ink-muted">…</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
